import { IEventEmitter, IsChromium, PlatformDetails, Log } from "../dependencies";
import { KeyDeprecatedStats, DeprecatedStats } from "./statsinterfaces";
import { EVENTS, StreamUpdateEvent } from "../interfaces";
import {
    INTERNAL_EVENTS,
    VideoFrameMetadata,
    VideoFrameRequestCallback,
    WithRequestVideoFrameCallback
} from "../internalinterfaces";
import { QosCalculator } from "./qoscalculator";
import { StreamStatsInternal } from "./streamstats";
import { RagnarokProfiler } from "../ragnarokprofiler";
import { RagnarokSettings } from "../util/settings";
import { BinaryReport, RTCInboundRtpStreamExtraStats } from "./binaryreport";
import { TelemetryHandler } from "../telemetry/telemetryhandler";
import { DataChannelParams, ExtendedDebugStats, StaticStreamStats } from "../rinterfaces";
import {
    ShouldEnableCPM,
    GetResolutionString,
    GetPhysicalResolution,
    GetLogicalResolution
} from "../util/utils";
import { CapabilitiesHelper } from "../util/devicecapabilities";
import { LDATOverlay, ILDATHandler } from "../debug/ldatoverlay";
import { NvstConfig } from "../nvstconfig";
import { DeviceStateObserver, DisplayTopology } from "../devicestateobserver";
import { RErrorCode } from "../rerrorcode";
import { Resolution, VideoCodecType } from "../nskinterfaces";
import { InputMediaElement, Measurements } from "../input/inputinterfaces";

const LOGTAG = "clientstatsservice";
const BWE_OFFSET = 1;
const JITTER_OFFSET = 9;
const RTD_OFFSET = 17;
const FPS_OFFSET = 25;

interface InboundVideoCounts {
    currReportFailed: number;
    prevReportFailed: number;
    success: number;
}

interface InboundVideoStats {
    counts: InboundVideoCounts;
    decoder: string;
    fallbackFrameNumber: number;
}

export interface IceConnectionStats {
    recentRequestsSent: number;
    recentResponsesReceived: number;
    recentPacketsReceived: number;
}

const enum StreamStatsState {
    OFF,
    STANDARD,
    DETAILED
}

const enum StatsCollectionState {
    STARTED,
    STREAMING,
    STOPPED
}

declare interface RTCIceCandidateStats extends RTCStats {
    transportId: string;
    address?: string | null;
    port?: number;
    protocol?: string;
    candidateType: RTCIceCandidateType;
    priority?: number;
    url?: string;
    // relayProtocol?: RTCIceServerTransportProtocol;
    foundation?: string;
    relatedAddress?: string;
    relatedPort?: number;
    usernameFragment?: string;
    tcpType?: RTCIceTcpCandidateType;
}

declare interface RTCIceCandidatePairStatsExtras extends RTCIceCandidatePairStats {
    requestsSent: number;
    consentRequestsSent: number;
    requestsReceived: number;
    responsesSent: number;
    responsesReceived: number;
    packetsSent: number;
    packetsReceived: number;
}

interface RtpMapping {
    rtp: number;
    client: number;
}

interface LocalStatistics {
    rafTime: number;
    droppedVideoFrames: number;
    totalVideoFrames: number;
}

const ICE_STATS_HISTORY_SIZE = 6;

export interface IClientStatsCallbacks {
    addDataChannel(dataChannel: RTCDataChannel, params: DataChannelParams): void;
    getExtendedDebugStats(): ExtendedDebugStats;
    writeEtwPrint(msg: string): void;
    processInputChannelStats(): void;
}
/**
 * Categories of ice candidates that can be generated/read from stats
 */
export const enum IceCandidateCategory {
    TCP = "TCP",
    MDNS = "MDNS",
    IPV6 = "IPV6",
    IPV4 = "IPV4",
    UNKNOWN = "UNKNOWN"
}

/**
 * Private IPv4 address ranges
 */
export const enum PrivateIPv4Range {
    TEN = "10/8",
    ONESEVENTWO = "172.16/12",
    ONENINETWO = "192.168/16",
    UNKNOWN = "UNKNOWN"
}

/**
 * Map that can be used to store counts of ice candidates categories, that guarantees keys exist for each enum
 * When a new IceCandidateCategory is added, must initialize its entry to 0 in the constructor
 */
export class IceCandidateCategoryCounts extends Map<IceCandidateCategory, number> {
    constructor() {
        super([
            [IceCandidateCategory.TCP, 0],
            [IceCandidateCategory.MDNS, 0],
            [IceCandidateCategory.IPV6, 0],
            [IceCandidateCategory.IPV4, 0],
            [IceCandidateCategory.UNKNOWN, 0]
        ]);
    }
}

/**
 *This class has all the logic to execute GetStats method, and calculate few stats for displaying on client side.
 */
export class ClientStatsService {
    private peerConnection: RTCPeerConnection;
    private telemetry: TelemetryHandler;
    private videoElement: HTMLVideoElement & WithRequestVideoFrameCallback;
    private running: boolean = true;
    private statsChannel!: RTCDataChannel;
    private statsIntervalId: number = 0;
    private deprecatedStatsIntervalId: number = 0;
    private eventEmitter: IEventEmitter;
    private keyDeprecatedStats: KeyDeprecatedStats;
    //stats cache from previous calls to calculate desired rates
    private videoPacketsReceived: number = 0;
    private videoFramesDecoded: number = 0;
    private audioCodecType: string = "";
    private videoCodecType: VideoCodecType = VideoCodecType.UNKNOWN;
    private reportCache?: RTCStatsReport;
    private qosCalculator: QosCalculator;
    private streamStats: StreamStatsInternal;
    private callbacks: IClientStatsCallbacks;
    private initTypeId: boolean = false;
    private typeToIdMap: Map<string, Array<string>> = new Map<string, Array<string>>();
    private bweMbps: number = 0;
    private jitter: number = 0;
    private rtd: number = 0;
    private gameFps: number = 0;
    private mediaType: "video" | "audio" = "video";
    private shouldEmitInternalStatsEvent: boolean;
    private binaryReport: BinaryReport;
    private streamStatsState: StreamStatsState = StreamStatsState.OFF;
    private nextStreamStatsTs: number = 0;
    private platformDetails: PlatformDetails;
    private usedHeapSize: number = 0;
    private totalHeapSize: number = 0;
    private staticStreamStats: StaticStreamStats;
    /// Maintains stats on the active candidate pair once a second for ICE_STATS_HISTORY_SIZE seconds
    private iceStats: RTCIceCandidatePairStatsExtras[] = [];
    private videoFrameCallbackFunc: VideoFrameRequestCallback;
    private logicalResolution: Resolution = { width: 0, height: 0 };
    private physicalResolution: Resolution = { width: 0, height: 0 };
    private pendingStats: boolean = false;
    private pendingDeprecatedStats: boolean = false;
    private inboundVideoStats: InboundVideoStats;
    private ldat?: LDATOverlay;
    private useInboundRtpAsTrack: boolean = false;
    private mapRtpTimestampsToFrames: boolean;
    private maxBitrate: number;
    private minBitrate: number;
    private lastUnsentRtpMapping?: RtpMapping;
    private nextRtpMappingTime: number = 0;
    private hasAudioStats?: boolean;
    private statsCollectionState: StatsCollectionState = StatsCollectionState.STARTED;
    private nextPeriodicStatPrintTime: number = 0;
    private localStats: LocalStatistics;
    private videoTagElement: InputMediaElement;
    private measurements: Measurements;
    private deviceObserver: DeviceStateObserver;
    private displayTopology: DisplayTopology = DisplayTopology.UNKNOWN;

    constructor(
        eventEmitter: IEventEmitter,
        callbacks: IClientStatsCallbacks,
        videoElement: HTMLVideoElement,
        pc: RTCPeerConnection,
        nvstConfig: NvstConfig,
        telemetry: TelemetryHandler,
        platformDetails: PlatformDetails,
        staticStreamStats: StaticStreamStats,
        deviceObserver: DeviceStateObserver,
        measurements: Measurements
    ) {
        this.eventEmitter = eventEmitter;
        this.callbacks = callbacks;
        this.videoElement = videoElement;
        this.peerConnection = pc;
        this.mapRtpTimestampsToFrames = nvstConfig.video?.[0].mapRtpTimestampsToFrames ?? false;
        this.maxBitrate = nvstConfig.video?.[0].maximumBitrateKbps ?? 0;
        this.minBitrate = nvstConfig.video?.[0].minimumBitrateKbps ?? 0;
        this.telemetry = telemetry;
        this.platformDetails = platformDetails;
        this.staticStreamStats = staticStreamStats;
        this.deviceObserver = deviceObserver;

        this.keyDeprecatedStats = {
            ts: 0,
            timingFrameInfo: "",
            targetDelayMs: 0,
            minPlayoutDelayMs: 0,
            currentDelayMs: 0
        };
        this.qosCalculator = new QosCalculator(this.maxBitrate, this.minBitrate);

        this.streamStats = new StreamStatsInternal(
            videoElement,
            this.platformDetails,
            this.staticStreamStats
        );
        if (RagnarokSettings.isInternalUser) {
            this.ldat = new LDATOverlay(videoElement, deviceObserver);
        }
        this.peerConnection = pc;
        this.createStatsDataChannel();
        this.shouldEmitInternalStatsEvent = this.eventEmitter.hasListener(
            INTERNAL_EVENTS.STREAMING_STATS
        );
        this.binaryReport = new BinaryReport();
        this.inboundVideoStats = {
            counts: {
                currReportFailed: 0,
                prevReportFailed: 0,
                success: 0
            },
            decoder: "",
            fallbackFrameNumber: -1
        };
        // local statistics
        this.localStats = {
            rafTime: 0,
            droppedVideoFrames: 0,
            totalVideoFrames: 0
        };
        this.videoTagElement = videoElement;
        this.measurements = measurements;

        this.videoFrameCallbackFunc = this.videoFrameCallback.bind(this);

        this.setIntervals();
    }

    private initMap() {
        this.peerConnection
            .getStats(null)
            .then((report: RTCStatsReport) => {
                let foundTrack = false;
                for (let stat of report.values()) {
                    if (stat.type === "track") {
                        foundTrack = true;
                    }
                    if (this.isTypeWhitelisted(stat.type)) {
                        if (stat.kind === "audio") {
                            this.hasAudioStats = true;
                        }

                        const statsArray = this.typeToIdMap.get(stat.type);
                        if (statsArray) {
                            if (!statsArray.includes(stat.id)) {
                                statsArray.push(stat.id);
                            } else {
                                Log.e("{5cea617}", "{76f5968}", stat.id);
                            }
                        } else {
                            this.typeToIdMap.set(stat.type, [stat.id]);
                        }
                    }
                }
                this.useInboundRtpAsTrack = !foundTrack;
                Log.i("{5cea617}", "{1317761}", (this.useInboundRtpAsTrack ? "yes" : "no"));
                this.initTypeId = true;
                this.reportCache = report;
                this.processStatsReport(report);
            })
            .catch(exp => this.emitStatsException(exp, "init"))
            .finally(() => this.finalizeGetStats());
    }

    public createStatsDataChannel() {
        let statsChannelParams = {
            ordered: false,
            reliable: false,
            maxRetransmits: 0
        };
        this.statsChannel = this.peerConnection.createDataChannel(
            "stats_channel",
            statsChannelParams
        );
        this.statsChannel.binaryType = "arraybuffer";
        this.callbacks.addDataChannel(this.statsChannel, {
            open: () => {
                this.statsCollectionState = StatsCollectionState.STREAMING;
            },
            close: () => {
                this.clearIntervals();
                this.statsCollectionState = StatsCollectionState.STOPPED;
            }
        });
        this.statsChannel.onmessage = (msg: any) => {
            var view = new DataView(msg.data);
            let version = view.getUint8(0);
            if (version >= 2) {
                this.bweMbps = view.getFloat64(BWE_OFFSET, true) / 1000000;
                this.jitter = view.getFloat64(JITTER_OFFSET, true) * 1000;
                this.rtd = view.getFloat64(RTD_OFFSET, true);

                this.streamStats.updateJitter(this.jitter);
                this.streamStats.updateRtd(this.rtd);
                if (version >= 3) {
                    this.gameFps = view.getFloat64(FPS_OFFSET, true);
                    this.streamStats.updateAvgGameFps(this.gameFps);
                }
                this.qosCalculator.calculateBandwidthScoreV2(
                    this.bweMbps,
                    this.streamStats.updateBwu(this.bweMbps)
                );
                this.qosCalculator.calculateLatencyScoreV2(this.rtd);
            } else {
                Log.e("{5cea617}", "{4f9f4b7}", version);
            }
        };
    }

    private setIntervals() {
        this.statsIntervalId = window.setInterval(() => {
            this.sendClientStats();
        }, RagnarokSettings.ragnarokConfig.getStatsInterval ?? 96);
        if (IsChromium()) {
            this.deprecatedStatsIntervalId = window.setInterval(() => {
                this.saveDeprecatedStats();
            }, RagnarokSettings.ragnarokConfig.getDeprecatedStatsInterval ?? 201);
        }

        this.videoElement.requestVideoFrameCallback?.(this.videoFrameCallbackFunc);
    }

    private videoFrameCallback(now: DOMHighResTimeStamp, metadata: VideoFrameMetadata) {
        this.binaryReport.sendVideoFrameMetadata(metadata);
        if (this.ldat?.isActive()) {
            this.ldat.onVideoFrame(now, metadata);
        }
        if (this.mapRtpTimestampsToFrames && metadata.rtpTimestamp && metadata.receiveTime) {
            this.lastUnsentRtpMapping = {
                rtp: metadata.rtpTimestamp,
                client: metadata.receiveTime - RagnarokProfiler.getStreamBeginTime()
            };
        }

        if (this.isEnabled()) {
            // Re-register the callback to be notified about the next frame.
            this.videoElement.requestVideoFrameCallback?.(this.videoFrameCallbackFunc);
        }
    }

    public preRender() {
        RagnarokProfiler.onPreRender();
        if (!RagnarokSettings.leanMode) {
            const now = window.performance.now();
            const frameCounts = getVideoFrameCounts(this.videoTagElement);
            if (frameCounts) {
                const totalDroppedVideoFrames = frameCounts.dropped;
                const droppedFrames = totalDroppedVideoFrames - this.localStats.droppedVideoFrames;
                const decodedFrames = frameCounts.decoded - this.localStats.totalVideoFrames;

                this.localStats.droppedVideoFrames = totalDroppedVideoFrames;
                this.localStats.totalVideoFrames = frameCounts.decoded;
                if (droppedFrames == 1) {
                    this.measurements.singleDroppedFrames++;
                } else if (droppedFrames > 1) {
                    this.measurements.multiDroppedFrames += droppedFrames;
                }
                RagnarokProfiler.onFrameInfo(decodedFrames, droppedFrames);
            }
            this.localStats.rafTime = now;
        }
    }

    public postRender() {
        if (!RagnarokSettings.leanMode) {
            this.handleMeasurements();
        }
    }

    private handleMeasurements() {
        const INTERVAL_MS = 10000;

        const now = window.performance.now();
        if (now > this.measurements.baseTime + INTERVAL_MS) {
            if (this.measurements.baseTime !== 0) {
                Log.i("{5cea617}", "{5fa9066}", this.measurements.animationFrameCount, this.measurements.sendInputCount, this.measurements.sendInputOver5ms, this.measurements.sendInputOver10ms, (this.localStats.totalVideoFrames -
                            this.measurements.baseTotalVideoFrames), this.measurements.singleDroppedFrames, this.measurements.multiDroppedFrames, this.measurements.aggregatedCount, this.measurements.oversizedEventCount);
            }

            this.measurements.baseTime = now;
            this.measurements.baseTotalVideoFrames = this.localStats.totalVideoFrames;
            this.measurements.animationFrameCount = 1;
            this.measurements.sendInputCount = 0;
            this.measurements.sendInputOver5ms = 0;
            this.measurements.sendInputOver10ms = 0;
            this.measurements.singleDroppedFrames = 0;
            this.measurements.multiDroppedFrames = 0;
            this.measurements.aggregatedCount = 0;
            this.measurements.oversizedEventCount = 0;
        } else {
            this.measurements.animationFrameCount += 1;
        }
    }

    private clearIntervals() {
        if (this.statsIntervalId) {
            clearInterval(this.statsIntervalId);
            this.statsIntervalId = 0;
        }
        if (this.deprecatedStatsIntervalId) {
            clearInterval(this.deprecatedStatsIntervalId);
            this.deprecatedStatsIntervalId = 0;
        }
        // We cache the per frame vfmd stats and send them in bursts
        // Ensure we flush any cached stats before disabling the stats
        this.binaryReport.sendCachedVfmdStats();
    }

    public reset() {
        this.clearIntervals();
        this.qosCalculator = new QosCalculator(this.maxBitrate, this.minBitrate);
        this.streamStats = new StreamStatsInternal(
            this.videoElement,
            this.platformDetails,
            this.staticStreamStats
        );
        this.reportCache = undefined;
    }

    public isEnabled() {
        return this.statsIntervalId != 0;
    }

    public disableStats() {
        if (!this.isEnabled()) {
            return;
        }
        this.reset();
        this.setOnScreenStats(false);
    }

    public enableStats() {
        if (this.isEnabled()) {
            return;
        }
        this.setIntervals();
    }

    public stop() {
        if (!this.running) {
            return;
        }
        this.running = false;
        this.disableStats();
        this.ldat?.stop();
        this.telemetry.emitMetricEvent(
            "InboundVideoStats",
            this.inboundVideoStats.decoder,
            this.inboundVideoStats.counts.success,
            this.inboundVideoStats.counts.currReportFailed,
            this.inboundVideoStats.fallbackFrameNumber,
            this.inboundVideoStats.counts.prevReportFailed
        );
    }

    private isTypeWhitelisted(type: string) {
        return ["transport", "inbound-rtp", "candidate-pair"].includes(type);
    }

    public resetTypeIds() {
        this.initTypeId = false;
    }

    private processEarlyStatsReport(report: RTCStatsReport) {
        this.reportCache = report;
    }

    private processStatsReport(report: RTCStatsReport) {
        const STREAM_STATS_INTERVAL_MS = 1000;
        const RTP_MAPPING_INTERVAL = 1000;
        const PERIODIC_STAT_PRINT_INTERVAL_MS = 5000;

        const getStatsT0 = performance.now();
        if (getStatsT0 >= this.nextStreamStatsTs && this.mediaType === "video") {
            this.updateOnScreenStats(report);
            this.updateCandidatePairStats(report);
            if (this.showStreamStats()) {
                this.streamStats.drawStatsOnScreen(
                    this.streamStatsState === StreamStatsState.DETAILED
                );
            }
            const streamEvent: StreamUpdateEvent = this.streamStats.makeStreamUpdateEvent();
            this.eventEmitter.emit(EVENTS.STREAM_STATS_UPDATE, streamEvent);
            this.reportCache = report;
            this.nextStreamStatsTs = getStatsT0 + STREAM_STATS_INTERVAL_MS;
        }
        this.convertReportToBinary(report);
        this.processGarbageCollectionStats();
        this.callbacks.processInputChannelStats();

        if (getStatsT0 >= this.nextRtpMappingTime && this.lastUnsentRtpMapping) {
            this.sendRtpMapping(this.lastUnsentRtpMapping);
            this.lastUnsentRtpMapping = undefined;

            this.nextRtpMappingTime = getStatsT0 + RTP_MAPPING_INTERVAL;
        }

        if (this.mediaType === "video") {
            if (ShouldEnableCPM(this.platformDetails)) {
                this.sendClientPerfStats();
            }
            this.streamStats.updateQScore(this.qosCalculator.GetStreamQuality());
            const sq = this.qosCalculator.GetStreamQuality();
            this.eventEmitter.emit(EVENTS.STREAMING_QUALITY, sq);
            if (this.shouldEmitInternalStatsEvent)
                this.eventEmitter.emit(
                    INTERNAL_EVENTS.STREAMING_STATS,
                    this.streamStats.getStreamingStats()
                );
            RagnarokProfiler.addQualityScore(sq);
        }

        if (getStatsT0 >= this.nextPeriodicStatPrintTime) {
            this.streamStats.printStats();
            this.nextPeriodicStatPrintTime = getStatsT0 + PERIODIC_STAT_PRINT_INTERVAL_MS;
        }

        const getStatsT1 = performance.now();
        let statsTs = getStatsT1 - getStatsT0;
        RagnarokProfiler.addGetStatsTime(statsTs);
        this.streamStats.updateStatsDuration(statsTs);
        if (this.hasAudioStats) {
            this.mediaType = this.mediaType === "video" ? "audio" : "video";
        }
    }

    private convertReportToBinary(report: RTCStatsReport) {
        for (const [type, ids] of this.typeToIdMap.entries()) {
            for (const id of ids) {
                const stats = report.get(id);
                if (!stats) {
                    continue;
                }
                switch (type) {
                    case "inbound-rtp":
                        this.binaryReport.sendInboundRtpStats(
                            this.getUnifiedInboundRtp(report, stats)
                        );
                        break;
                    default:
                        break;
                }
            }
        }
    }

    public updateDcSendDuration(time: number) {
        this.streamStats.updateDcSendDuration(time);
    }
    public updateBlockedDuration(duration: number) {
        this.streamStats.updateMainThreadBlockDuration(duration);
    }
    public sendClientStats() {
        // Don't start another call to getStats if the previous one hasn't succeeded. This indicates a client perf
        // problem which we shouldn't make worse. This is also especially important for initMap() to ensure we don't
        // add the same IDs to the map multiple times
        if (this.pendingStats) {
            return;
        }
        this.pendingStats = true;

        if (this.statsCollectionState === StatsCollectionState.STARTED) {
            // Connection is still being established, cache reports so we can look for clues if it fails
            // But don't initialize our stat map/stream stats because the connection is not finalized
            this.peerConnection
                .getStats()
                .then(report => this.processEarlyStatsReport(report))
                .catch(exp => this.emitStatsException(exp, "early"))
                .finally(() => this.finalizeGetStats());
        } else if (this.statsCollectionState === StatsCollectionState.STREAMING) {
            if (!this.initTypeId) {
                this.initMap();
            } else {
                const receivers = this.peerConnection.getReceivers();
                const receiver = receivers.find(
                    receiver => receiver.track.kind === this.mediaType && receiver.track.enabled
                );
                if (!receiver) return;
                receiver
                    .getStats()
                    .then(report => this.processStatsReport(report))
                    .catch(exp => this.emitStatsException(exp, "standard"))
                    .finally(() => this.finalizeGetStats());
            }
        } else {
            this.pendingStats = false;
        }
    }

    private showStreamStats(): boolean {
        return this.streamStatsState !== StreamStatsState.OFF;
    }

    /**
     * Change the display status of the client side stats display
     * @param enableDevStats Whether to enable to advanced developer or basic stats display. Default is false.
     * @param enable Whether to enable or disable the display. If not provided, will invert the status.
     * @todo Make enable required when GridApp is deprecated
     */
    public toggleOnScreenStats(enableDevStats: boolean, enable?: boolean) {
        this.setOnScreenStats(enable ?? !this.showStreamStats(), enableDevStats);
    }

    public sendQosMarker() {
        RagnarokProfiler.addQosMarker();
        this.streamStats.updateQosMarkerCount();
        this.streamStats.refreshText(this.streamStatsState === StreamStatsState.DETAILED);
    }

    private setOnScreenStats(show: boolean, enableDevStats: boolean = false) {
        this.streamStats.setShown(show);
        if (show) {
            this.streamStatsState = enableDevStats
                ? StreamStatsState.DETAILED
                : StreamStatsState.STANDARD;
            this.streamStats.drawStatsOnScreen(enableDevStats);
            // Trigger an immediate stats update
            this.nextStreamStatsTs = performance.now();
        } else {
            this.streamStatsState = StreamStatsState.OFF;
        }
    }

    private updateOnScreenStats(report: RTCStatsReport) {
        this.updateInboundVideoRtpStats(report);
        this.streamStats.updateExtendedDebugStats(this.callbacks.getExtendedDebugStats());
    }

    private updateInboundVideoRtpStats(report: RTCStatsReport) {
        const ids = this.typeToIdMap.get("inbound-rtp");
        if (!ids || !this.reportCache) return;
        let inboundVideo: RTCInboundRtpStreamExtraStats | undefined = ids
            .map(id => report.get(id))
            .find(x => x?.kind === "video" || x?.mediaType === "video");
        if (!inboundVideo) {
            this.inboundVideoStats.counts.currReportFailed++;
            return;
        }
        inboundVideo = this.getUnifiedInboundRtp(report, inboundVideo);
        const decoder: string | undefined = inboundVideo.decoderImplementation;
        if (decoder?.indexOf) {
            const isFallback = decoder.indexOf("fallback") !== -1;
            if (isFallback) {
                if (this.inboundVideoStats.fallbackFrameNumber === -1) {
                    this.inboundVideoStats.fallbackFrameNumber =
                        inboundVideo.framesDecoded ?? this.videoFramesDecoded;
                    // TODO: Remove from blob stats after qosweb sources from ETWPrint
                    RagnarokProfiler.onEvent("SoftwareDecodeFallback");
                    const ETWS_SOFTWARE_DECODE_FALLBACK =
                        "Fallback to software decode at frame " +
                        String(this.inboundVideoStats.fallbackFrameNumber);
                    Log.d("{5cea617}", "{0b0c6f9}", ETWS_SOFTWARE_DECODE_FALLBACK);
                    this.streamStats.setSoftwareDecodeFallback(true);
                    this.callbacks.writeEtwPrint(ETWS_SOFTWARE_DECODE_FALLBACK);
                }
            }
            if (decoder !== this.inboundVideoStats.decoder && decoder !== "unknown") {
                this.inboundVideoStats.decoder = decoder;
                this.streamStats.setDecoderImplementation(decoder);
            }
        }
        const prevInboundVideo: RTCInboundRtpStreamExtraStats | undefined = this.reportCache.get(
            inboundVideo.id
        );
        if (!prevInboundVideo) {
            this.inboundVideoStats.counts.prevReportFailed++;
            return;
        }
        this.inboundVideoStats.counts.success++;
        const codecId = inboundVideo.codecId;
        if (codecId) {
            const mimeCodec = report.get(codecId).mimeType.toUpperCase();
            if (mimeCodec.includes("H265")) {
                this.videoCodecType = VideoCodecType.H265;
            } else if (mimeCodec.includes("AV1")) {
                this.videoCodecType = VideoCodecType.AV1;
            } else if (mimeCodec.includes("H264")) {
                this.videoCodecType = VideoCodecType.H264;
            } else {
                Log.d("{5cea617}", "{4b31535}", mimeCodec);
                this.videoCodecType = VideoCodecType.UNKNOWN;
            }
            this.streamStats.updateVideoCodec(this.videoCodecType);
        }
        this.qosCalculator.calculateNetworkLossScore(inboundVideo, prevInboundVideo);
        this.streamStats.updateInboundVideoRtpStats(inboundVideo);
        this.videoFramesDecoded = inboundVideo.framesDecoded ?? 0;
        this.videoPacketsReceived = inboundVideo.packetsReceived ?? 0;
        this.updateDisplayDetails();
    }

    // If we are using track stats, grabs them from the report and combines it with the given inbound RTP stats.
    // This basically emulates the newer inbound RTP stats that have all the fields taken from track stats
    private getUnifiedInboundRtp(
        report: RTCStatsReport,
        inboundRtp: RTCInboundRtpStreamExtraStats
    ): RTCInboundRtpStreamExtraStats {
        if (!this.useInboundRtpAsTrack) {
            const track: RTCInboundRtpStreamExtraStats | undefined = report.get(
                (<any>inboundRtp).trackId
            );
            if (track) {
                // We use track as the target rather than inboundRtp because we want any existing inbound RTP fields
                // to overwrite the deprecated ones in track
                inboundRtp = Object.assign(track, inboundRtp);
            }
        }
        return inboundRtp;
    }

    private updateCandidatePairStats(report: RTCStatsReport) {
        const ids = this.typeToIdMap.get("candidate-pair");
        const candidatePair: RTCIceCandidatePairStatsExtras | undefined = ids
            ?.map(id => report.get(id))
            .find(x => x);
        if (!candidatePair) {
            return;
        }
        if (this.iceStats.length >= ICE_STATS_HISTORY_SIZE) {
            this.iceStats.splice(0, 1);
        }
        this.iceStats.push(candidatePair);

        const prevCandidatePair: RTCIceCandidatePairStatsExtras | undefined = this.reportCache?.get(
            candidatePair.id
        );
        if (!prevCandidatePair) {
            return;
        }
        this.streamStats.updateAvgStreamingRate(candidatePair, prevCandidatePair);
    }

    private saveDeprecatedStats() {
        if (this.pendingDeprecatedStats) {
            return;
        }
        this.pendingDeprecatedStats = true;
        const rejectionHandler = () => {
            Log.i("{5cea617}", "{cf8b53f}");
            this.pendingDeprecatedStats = false;
            clearInterval(this.deprecatedStatsIntervalId);
            this.deprecatedStatsIntervalId = 0;
        };
        (<any>this.peerConnection)
            .getStats((report: DeprecatedStats) => {
                for (let stat of report.result()) {
                    if (stat.type != "ssrc") continue;
                    for (let name of stat.names()) {
                        if (stat.stat("mediaType") === "video") {
                            this.keyDeprecatedStats.ts = RagnarokProfiler.getStreamTime();
                            this.keyDeprecatedStats.timingFrameInfo =
                                stat.stat("googTimingFrameInfo");
                            this.keyDeprecatedStats.targetDelayMs = +stat.stat("googTargetDelayMs");
                            this.keyDeprecatedStats.minPlayoutDelayMs =
                                +stat.stat("googMinPlayoutDelayMs");
                            this.keyDeprecatedStats.currentDelayMs =
                                +stat.stat("googCurrentDelayMs");
                        }
                        break;
                    }
                }
                this.binaryReport.sendDeprStats(this.keyDeprecatedStats);
                this.pendingDeprecatedStats = false;
            }, rejectionHandler)
            .catch(rejectionHandler);
    }

    private sendClientPerfStats() {
        const APP_FEEDBACK_TYPE = 1;
        const APP_FEEDBACK_VERSION = 1;

        const dataBuffer = new ArrayBuffer(12);
        const dataBufferView = new DataView(dataBuffer);
        const offset = 0;

        dataBufferView.setUint8(offset, APP_FEEDBACK_TYPE);
        dataBufferView.setUint8(offset + 1, APP_FEEDBACK_VERSION);
        dataBufferView.setUint16(offset + 2, this.binaryReport.getPacketsLost(), true);
        dataBufferView.setUint16(offset + 4, this.binaryReport.getDecodeTimeAvgMs(), true);
        dataBufferView.setUint16(offset + 6, this.binaryReport.getFramesDecoded(), true);
        dataBufferView.setUint16(offset + 8, this.binaryReport.getPliCount(), true);
        dataBufferView.setUint16(offset + 10, this.binaryReport.getFramesDropped(), true);

        this.maybeSendMessage(dataBuffer, "clientperf");
    }

    private sendRtpMapping(mapping: RtpMapping) {
        const RTP_MAPPING_TYPE = 2;
        const RTP_MAPPING_VERSION = 1;
        // We can support multiple timestamps in one message by making this dynamic in the future
        const RTP_COUNT = 1;

        const dataBuffer = new ArrayBuffer(15);
        const dataBufferView = new DataView(dataBuffer);
        const offset = 0;

        dataBufferView.setUint8(offset, RTP_MAPPING_TYPE);
        dataBufferView.setUint8(offset + 1, RTP_MAPPING_VERSION);
        dataBufferView.setUint8(offset + 2, RTP_COUNT);
        dataBufferView.setUint32(offset + 3, mapping.rtp, true);
        dataBufferView.setFloat64(offset + 7, mapping.client, true);

        this.maybeSendMessage(dataBuffer, "rtpmapping");
    }

    private maybeSendMessage(buffer: ArrayBuffer, where: string): void {
        try {
            if (this.statsChannel.readyState === "open") {
                this.statsChannel.send(buffer);
            }
        } catch (exp) {
            let msg = "statsChannel send exception";
            Log.e("{5cea617}", "{78e74c5}", exp);
            this.emitStatsException(exp, where);
        }
    }

    private emitStatsException(exp: Error | DOMException, where: string) {
        this.telemetry.emitExceptionEvent(exp,
            `Exception in ${where} getStats`,
            LOGTAG + ".ts",
            0,
            0,
            true,
            "getStats");
    }

    private finalizeGetStats(): void {
        this.pendingStats = false;
    }

    private processGarbageCollectionStats() {
        let memoryInfo = (performance as any).memory;
        if (!memoryInfo) {
            return;
        }

        const currentUsedHeapSize = memoryInfo.usedJSHeapSize;
        const currentTotalHeapSize = memoryInfo.totalJSHeapSize;

        let deltaUsedHeapSize = currentUsedHeapSize - this.usedHeapSize;
        let deltaTotalHeapSize = currentTotalHeapSize - this.totalHeapSize;

        this.usedHeapSize = currentUsedHeapSize;
        this.totalHeapSize = currentTotalHeapSize;

        // only report when there is a decrease in heap size
        if (deltaUsedHeapSize >= 0 && deltaTotalHeapSize >= 0) {
            return;
        }
        RagnarokProfiler.addGarbageCollectionStats(deltaUsedHeapSize, deltaTotalHeapSize);
    }

    public getFramesDecoded(): number {
        return this.videoFramesDecoded;
    }

    public getVideoCodec(): VideoCodecType {
        return this.videoCodecType;
    }
    public getAudioCodec(): string {
        return this.audioCodecType;
    }

    public packetsReceived(): number {
        return this.videoPacketsReceived;
    }

    public getIceStats(): IceConnectionStats | undefined {
        const handleNan = (x: number) => (isNaN(x) ? -1 : x);

        if (this.iceStats.length < 2) {
            return undefined;
        }
        const oldPair = this.iceStats[0];
        const newPair = this.iceStats[this.iceStats.length - 1];
        return {
            recentRequestsSent: handleNan(
                newPair.requestsSent -
                    oldPair.requestsSent +
                    newPair.consentRequestsSent -
                    oldPair.consentRequestsSent
            ),
            recentResponsesReceived: handleNan(
                newPair.responsesReceived - oldPair.responsesReceived
            ),
            recentPacketsReceived: handleNan(newPair.packetsReceived - oldPair.packetsReceived)
        };
    }

    public getCandidatePairError(): RErrorCode | undefined {
        if (!this.reportCache) {
            return undefined;
        }
        let totalRequestsSent = 0;
        let totalResponsesReceived = 0;
        // Currently OneSDK doesn't send any STUN requests, only responses. When OneSDK fully implements ICE, we will
        // be able to interpret not receiving any requests as an error
        let _totalRequestsReceived = 0;
        let _totalResponsesSent = 0;
        let totalNominatedPairs = 0;
        let totalSucceededPairs = 0;
        for (const stat of this.reportCache.values()) {
            if (stat.type === "candidate-pair") {
                const pair = <RTCIceCandidatePairStatsExtras>stat;
                totalRequestsSent += pair.requestsSent + pair.consentRequestsSent;
                totalResponsesReceived += pair.responsesReceived;
                _totalRequestsReceived += pair.requestsReceived;
                _totalResponsesSent += pair.responsesSent;
                if (pair.nominated) {
                    totalNominatedPairs++;
                }
                if (pair.state === "succeeded") {
                    totalSucceededPairs++;
                }
            }
        }

        Log.i("{5cea617}", "{47c0ce7}", totalRequestsSent, totalResponsesReceived);

        if (totalRequestsSent > 0 && totalResponsesReceived === 0) {
            return RErrorCode.StreamerNoStunResponsesReceived;
        } else if (!totalNominatedPairs) {
            return RErrorCode.StreamerNoNominatedCandidatePairs;
        } else if (!totalSucceededPairs) {
            return RErrorCode.StreamerNoSucceededCandidatePairs;
        }
        return undefined;
    }

    public getCandidateStatCategoryCounts(): IceCandidateCategoryCounts {
        let candidateCounts = new IceCandidateCategoryCounts();

        if (!this.reportCache) {
            return candidateCounts;
        }

        for (const stat of this.reportCache.values()) {
            if (stat.type === "local-candidate") {
                const candidateStat = <RTCIceCandidateStats>stat;
                const category = getIceCandidateCategory(candidateStat);
                candidateCounts.set(category, candidateCounts.get(category)! + 1);
            }
        }

        return candidateCounts;
    }

    public getCandidateAddressRanges(): Set<PrivateIPv4Range> {
        let candidateAddressRanges = new Set<PrivateIPv4Range>();

        if (!this.reportCache) {
            return candidateAddressRanges;
        }

        for (const stat of this.reportCache.values()) {
            if (stat.type === "local-candidate") {
                const candidateStat = <RTCIceCandidateStats>stat;
                if (getIceCandidateCategory(candidateStat) === IceCandidateCategory.IPV4) {
                    const candidateRange = getIceCandidateAddressRange(candidateStat);
                    if (candidateRange !== PrivateIPv4Range.UNKNOWN) {
                        // don't count unknown ranges, because they could be public IPs
                        candidateAddressRanges.add(candidateRange);
                    }
                }
            }
        }

        return candidateAddressRanges;
    }

    private async updateDisplayDetails() {
        const physicalResolution = GetPhysicalResolution();
        const logicalResolution = GetLogicalResolution();
        const videoFramesDecoded = this.videoFramesDecoded;
        const displayTopology = this.deviceObserver.getDisplayTopology();

        const isEqual = (x: Resolution, y: Resolution) => {
            return x.width === y.width && x.height === y.height;
        };

        if (
            isEqual(physicalResolution, this.physicalResolution) &&
            isEqual(logicalResolution, this.logicalResolution) &&
            displayTopology === this.displayTopology
        ) {
            return;
        }

        this.physicalResolution = physicalResolution;
        this.logicalResolution = logicalResolution;
        const refreshRate = await CapabilitiesHelper.getRefreshRate();
        this.displayTopology = displayTopology;

        const ETWS_DISPLAY_RESOLUTION = `Stream[0]: Client display[-1] resolution : {physical : ${GetResolutionString(
            physicalResolution
        )}@${refreshRate}, logical : ${GetResolutionString(
            logicalResolution
        )}@${refreshRate}} at frame#${videoFramesDecoded} topology: ${displayTopology}`;

        Log.i("{5cea617}", "{0b0c6f9}", ETWS_DISPLAY_RESOLUTION);
        this.callbacks.writeEtwPrint(ETWS_DISPLAY_RESOLUTION);
    }

    public getLdatHandler(): ILDATHandler | undefined {
        return this.ldat;
    }
}

function getVideoFrameCounts(
    videoTagElement: InputMediaElement
): { decoded: number; dropped: number } | undefined {
    if (!!videoTagElement.getVideoPlaybackQuality) {
        const quality = videoTagElement.getVideoPlaybackQuality();
        return {
            decoded: quality.totalVideoFrames,
            dropped: quality.droppedVideoFrames
        };
    } else if (videoTagElement.webkitDecodedFrameCount !== undefined) {
        return {
            decoded: videoTagElement.webkitDecodedFrameCount,
            dropped: videoTagElement.webkitDroppedFrameCount ?? 0
        };
    }
    return undefined;
}

/**
 * @param candidate Ice candidate or candidate stat to check the candidate category of
 * @return Category of the ice candidate
 */
export function getIceCandidateCategory(
    candidate: RTCIceCandidate | RTCIceCandidateStats
): IceCandidateCategory {
    if (candidate.protocol === "tcp") {
        return IceCandidateCategory.TCP;
    }
    const address = candidate.address || candidate.relatedAddress;
    if (!address) {
        return IceCandidateCategory.UNKNOWN;
    } else if (address.endsWith(".local")) {
        return IceCandidateCategory.MDNS;
    } else if (address.indexOf(":") !== -1) {
        return IceCandidateCategory.IPV6;
    } else {
        return IceCandidateCategory.IPV4;
    }
}

/**
 * @param candidate Ice candidate or candidate stat to check the address range of
 * @return Private IPv4 range the candidate falls into, or undefined if it does not fall into a category
 */
export function getIceCandidateAddressRange(
    candidate: RTCIceCandidate | RTCIceCandidateStats
): PrivateIPv4Range {
    const address = candidate.address || candidate.relatedAddress;
    const octets = address?.split(".");
    if (octets?.length === 4) {
        const firstOctet = parseInt(octets[0]);
        const secondOctet = parseInt(octets[1]);
        if (!isNaN(firstOctet) && !isNaN(secondOctet)) {
            let range = PrivateIPv4Range.UNKNOWN;
            if (firstOctet === 10) {
                // 10/8: first octet must be 10
                range = PrivateIPv4Range.TEN;
            } else if (firstOctet === 192 && secondOctet === 168) {
                // 192.168/16: first two octets must be 192 and 168
                range = PrivateIPv4Range.ONENINETWO;
            } else if (firstOctet === 172 && (secondOctet & 0xf0) === 16) {
                // 172.16/12: first octet must be 172 and the top four bits of the second octet must be 16
                range = PrivateIPv4Range.ONESEVENTWO;
            }
            if (range !== PrivateIPv4Range.UNKNOWN) {
                Log.d("{5cea617}", "{343589d}", range);
            }
            return range;
        }
    }
    Log.i("{5cea617}", "{fa2d6b9}");
    return PrivateIPv4Range.UNKNOWN;
}
